import { atom, getDefaultStore } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import type { Permission, Session, User } from '@/types/auth.types'

/**
 * Persisted session — survives page reloads via localStorage under "AccessToken".
 *
 * NOTE: atomWithStorage stores the JSON-stringified Session object, not just the token.
 * That means the legacy pattern `localStorage.getItem('AccessToken')` won't return a
 * raw token string. Always read through this atom (via `getDefaultStore().get(sessionAtom)`
 * outside React, or `useAtomValue(sessionAtom)` inside).
 */
export const sessionAtom = atomWithStorage<Session | null>(
    'AccessToken',
    null,
    undefined,
    { getOnInit: true },
)

/** True once the app has finished its initial auth bootstrap check. Resets on every reload. */
export const isInitializedAtom = atom(false)

/** Read-only: is there a session at all? */
export const isAuthenticatedAtom = atom((get) => get(sessionAtom) !== null)

/** Read-only: current user, or null. */
export const currentUserAtom = atom<User | null>((get) => get(sessionAtom)?.user ?? null)

/** Read-only: permissions array, or empty. Empty array (not null) so callers can `.includes()` safely. */
export const permissionsAtom = atom<Permission[]>((get) => get(sessionAtom)?.permissions ?? [])

/**
 * Resolves once isInitializedAtom flips to true.
 * Used by route guards to wait for the initial auth bootstrap.
 *
 * Why not use Suspense? Route guards in beforeLoad can't suspend —
 * they're plain async functions evaluated outside React.
 */
export async function waitForAuthInit(): Promise<void> {
    // SSR: there's no session to validate (no localStorage), and the bootstrap
    // doesn't run on the server. Don't block here — let the client's beforeLoad
    // re-run after hydration with the real auth state.
    if (typeof window === 'undefined') return

    const store = getDefaultStore()
    if (store.get(isInitializedAtom)) return

    return new Promise<void>((resolve) => {
        const unsub = store.sub(isInitializedAtom, () => {
            if (store.get(isInitializedAtom)) {
                unsub()
                resolve()
            }
        })
    })
}

export const sessionExpiredAtom = atom(false)